import * as React from 'react';
import './index.scss';
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from 'react-beautiful-dnd';
import { Avatar, Button, Card, Flex, Input, notification, Space, Tooltip, Typography } from 'antd'
import { PlusOutlined, UserOutlined } from '@ant-design/icons'
import Filter from '../filter';
import { useDispatch } from 'react-redux';
import { postData, putData } from '../../../../../actions/developer';
import { getData } from '../../../../../actions/apiCall';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { constants } from '../../../../../constants/common';

const { Title, Text } = Typography;
const { useState, useEffect } = React;

interface BoardComponentProps {
    onClickIssue: (e) => void;
    projectId: string;
}

interface SprintProps {
    _id: string;
    name: string;
    startDate: string;
    endDate: string;
}

interface TaskList {
    _id: string;
    columnId: string;
    title: string;
    projectId: string;
    order: number;
    assignee: {
        avatar?: string;
        _id?: string;
        firstName?: string;
        lastName?: string;
    } | null;
}

interface TaskProps {
    columnId: string;
    tasks: TaskList[]
}

const BoardComponent: React.FC<BoardComponentProps> = ({ onClickIssue, projectId }) => {
    const [editableColomn, setEditableColomn] = useState<string>(null);
    const [addColumn, setAddColumn] = useState<boolean>(false);
    const [columnValue, setColumnValue] = useState<string>('');
    const [columnData, setColumnData] = useState<any>([]);
    const [tasks, setTasks] = useState<TaskProps[]>([]);
    const [taskTitle, setTaskTitle] = useState<string>('');
    const [isCreateLoading, setIsCreateLoading] = useState<boolean>(false);
    const [sprintData, setSprintData] = useState<SprintProps>(null);
    const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
    const { reloadFor } = useSelector((state: any) => state.app);

    const dispatch = useDispatch();

    useEffect(() => {
        projectId && getColumnData();
        if (projectId) {
            getColumnData();
            getTaskList()
        }
    }, [projectId]);

    useEffect(() => {
        if (reloadFor && reloadFor === 'Task') {
            getTaskList();
            dispatch({ type: constants.REFRESH_DATA, reloadFor: null })
        }
    }, [reloadFor]);


    useEffect(() => {
        getTaskList()
    }, [selectedTeamMembers]);


    const sort = (a, b) => a.order - b.order;

    const getColumnData = async () => {
        const resData = await dispatch(getData(`/columns/${projectId}`));
        if (resData.code == 200) {
            setColumnData(resData.data.sort(sort));
        }
    }

    const getTaskList = async () => {
        const resData = await dispatch(getData(`/tasks/${projectId}`, { teamMembers: selectedTeamMembers }));
        if (resData.code == 200) {
            resData.data.taskData.forEach((column) => column.tasks.sort(sort));
            setTasks(resData.data.taskData.sort(sort));
            setSprintData(resData.data.activeSprint);
        }
    }

    const addTask = async (columnId) => {
        if (!taskTitle || taskTitle === '') {
            return notification.error({ message: 'Please enter task title' });
        }
        setIsCreateLoading(true);
        try {
            const resData = await dispatch(postData('/tasks', {
                columnId,
                projectId: projectId,
                title: taskTitle,
                sprintId: sprintData._id,
                order: tasks.find(ele => ele.columnId == columnId).tasks.length
            }));
            if (resData.code == 200) {
                getTaskList();
                onCancel();
            } else {
                notification.error({ message: resData.message });
            }
        } catch (error) {
            notification.error({ message: 'Something went wrong' });
        } finally {
            setIsCreateLoading(false);
        }
    }

    const onCancel = () => {
        setTaskTitle('');
        setEditableColomn(null);
    }

    const onSubmitColumn = async () => {
        if (columnValue) {
            const resData = await dispatch(postData('/columns', { name: columnValue, projectId }));
            if (resData.code == 200) {
                getColumnData();
                setColumnValue('');
                setAddColumn(false);
            }
        } else {
            notification.error({ message: 'Please enter column title' });
        }
    }

    const onChangeColumn = async (e, columnId) => {
        if (!e || e === '') {
            return
        }
        const resData = await dispatch(putData(`/columns/${columnId}`, { name: e }));
        if (resData) {
            getColumnData();
        }
    }

    const updateTaskStatus = async (value, task) => {
        if (!value || value === task.title || value === '') {
            return
        }
        const resData = await dispatch(putData(`/tasks/${task._id}`, { title: value }));
        if (!resData) {
            notification.error({ message: 'Error', description: 'Failed to update task status' });
        } else {
            getTaskList();
        }
    }

    const onDragEnd = (result: any) => {
        const { source, destination, type } = result;
        if (!destination) {
            return;
        }

        if (destination.index === source.index && destination.droppableId === source.droppableId) {
            return;
        }

        if (type === "COLUMN") {
            const reorderedColumns = Array.from(columnData);
            const [movedColumn] = reorderedColumns.splice(source.index, 1);
            reorderedColumns.splice(destination.index, 0, movedColumn);
            reorderedColumns.forEach((column: any, index) => column.order = index);
            setColumnData(reorderedColumns);
            updateOrder(reorderedColumns)
        } else {
            const sourceColumnId = source.droppableId;
            const destinationColumnId = destination.droppableId;
            const updatedTasks = [...tasks];
            const sourceColumn = updatedTasks.find((column) => column.columnId === sourceColumnId);
            const destinationColumn = updatedTasks.find((column) => column.columnId === destinationColumnId);
            if (sourceColumn && destinationColumn) {
                const [movedTask] = sourceColumn.tasks.splice(source.index, 1);
                destinationColumn.tasks.splice(destination.index, 0, { ...movedTask, columnId: destinationColumnId });
                updatedTasks.forEach((column) =>
                    column.tasks.map((task, index) => {
                        task.order = index;
                    }));
                setTasks(updatedTasks);
                updateTaskOrder(updatedTasks);
            } else if (sourceColumn && !destinationColumn) {
                const [movedTask] = sourceColumn.tasks.splice(source.index, 1);
                const newTask: TaskProps = {
                    tasks: [{ ...movedTask, order: 0, columnId: destinationColumnId }],
                    columnId: destinationColumnId,
                };
                updatedTasks.push(newTask);
                setTasks(updatedTasks);
                updateTaskOrder(updatedTasks);
            }
        }
    };

    const updateOrder = async (orderData) => {
        const resData = await dispatch(putData(`/columns/bulk/update`, orderData));
        if (resData) {
            getColumnData();
        }
    }

    const updateTaskOrder = async (orderData) => {
        const tasks = orderData.map((column) => column.tasks).flat();
        const resData = await dispatch(putData(`/tasks/bulk/update`, tasks));
        if (resData) {
            getTaskList();
        }
    }

    const onClickCreateIssue = (columnId) => {
        setEditableColomn(columnId)
    }

    const getCloseDate = (date) => {
        return dayjs(date).diff(dayjs(), 'day') + ' days remaining';
    }

    const handleEditStart = (e) => {
        e.stopPropagation();
    }

    return (
        <>
            <Flex justify='space-between'>
                <Filter projectId={projectId} onSearch={(e) => setSelectedTeamMembers(e)} />
                {sprintData && <Space direction='horizontal' align='center'>
                    <Typography.Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>{sprintData?.name}</Typography.Title>
                    <Typography.Text>{getCloseDate(sprintData?.endDate)}</Typography.Text>
                </Space>}
            </Flex>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="COLUMN" type="COLUMN" direction='horizontal'>
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            <div className='task-board'>
                                {columnData.map((column: { _id: string, name: string }, index) => {
                                    return (
                                        <>
                                            <Draggable key={column._id} draggableId={column._id} index={index}>
                                                {(provided) => (
                                                    <div
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        ref={provided.innerRef}
                                                        style={{
                                                            margin: '0 0 8px 0',
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        <div className='dropabale_block'>
                                                            <Title level={5} editable={{
                                                                triggerType: ['text'],
                                                                onChange: (e) => column.name !== e && onChangeColumn(e, column._id)
                                                            }}
                                                                style={{ margin: 0 }}
                                                            >
                                                                {column.name}
                                                            </Title>
                                                            <div style={{
                                                                overflowY: 'auto',
                                                                height: '95%'
                                                            }}>
                                                                <Droppable droppableId={column._id} type={'Child'}>
                                                                    {(provided) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.droppableProps}
                                                                            style={{
                                                                                marginTop: 20,
                                                                                marginBottom: 20,
                                                                            }}
                                                                        >
                                                                            {tasks.find(ele => ele.columnId == column._id)?.tasks?.map((task, index) => (
                                                                                <Draggable key={task._id} draggableId={task._id} index={index}>
                                                                                    {(provided) => (
                                                                                        <div
                                                                                            {...provided.draggableProps}
                                                                                            {...provided.dragHandleProps}
                                                                                            ref={provided.innerRef}
                                                                                            style={{
                                                                                                margin: '0 0 8px 0',
                                                                                                ...provided.draggableProps.style,
                                                                                            }}
                                                                                        >
                                                                                            <Card className='task-card-padding' onClick={() => onClickIssue(task._id)}>
                                                                                                <Space direction='vertical' style={{ width: '100%' }}>
                                                                                                    <div
                                                                                                        onClick={handleEditStart}
                                                                                                        style={{ display: 'inline-block', cursor: 'pointer' }}
                                                                                                    >
                                                                                                        <Title level={5} editable={{
                                                                                                            triggerType: ['text'],
                                                                                                            onChange: (e) => task.title !== e && updateTaskStatus(e, task)
                                                                                                        }} className='title-edit'>{task.title}</Title>
                                                                                                    </div>
                                                                                                    <Flex justify='space-between'>
                                                                                                        <Text type='secondary'>Task</Text>
                                                                                                        <Tooltip title={task?.assignee?.firstName && task?.assignee?.firstName + ' ' + task?.assignee?.lastName}>
                                                                                                            <Avatar icon={!task?.assignee?.firstName && <UserOutlined />} size={'small'}
                                                                                                                src={`${process.env.FILE_URL}${task?.assignee?.avatar}`}
                                                                                                                onError={() => true}
                                                                                                            >
                                                                                                                {task?.assignee?.firstName && task?.assignee?.firstName[0] + task?.assignee?.lastName[0]}
                                                                                                            </Avatar>
                                                                                                        </Tooltip>
                                                                                                    </Flex>
                                                                                                </Space>
                                                                                            </Card>
                                                                                        </div>
                                                                                    )}
                                                                                </Draggable>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </Droppable>
                                                                <div style={{ marginTop: 10 }}>
                                                                    {column._id != editableColomn && <div className='d_hover'>
                                                                        <Button type='text' iconPosition='start' onClick={() => onClickCreateIssue(column._id)} icon={<PlusOutlined />}>Create a Issue</Button>
                                                                    </div>}
                                                                    {column._id == editableColomn && <Card className='create_task' style={{ height: '100px', border: '2px solid #2684FF' }}>
                                                                        <Space direction='vertical' size={'large'} style={{ width: '100%' }}>
                                                                            <Input placeholder='Whats need to be done?' onChange={(e) => setTaskTitle(e.target.value)} />
                                                                            <Flex justify='space-between'>
                                                                                <Text>Task</Text>
                                                                                <Space>
                                                                                    <Button type='text' onClick={() => addTask(column._id)} loading={isCreateLoading}>Create</Button>
                                                                                    <Button type='text' onClick={() => onCancel()}>Cancel</Button>
                                                                                </Space>
                                                                            </Flex>
                                                                        </Space>
                                                                    </Card>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        </>
                                    );
                                })}
                                {addColumn && <Space align='start' direction='horizontal'>
                                    <Input placeholder='Column Title' onChange={(e) => setColumnValue(e.target.value)} style={{ width: 200 }} />
                                    <Button onClick={onSubmitColumn}>Save</Button>
                                    <Button onClick={() => setAddColumn(false)} danger>Cancel</Button>
                                </Space>}
                                {!addColumn && <Button type='text' onClick={() => setAddColumn(true)}>Add Column</Button>}
                            </div>
                        </div>
                    )}
                </Droppable>

            </DragDropContext>
        </>
    );
};

export default BoardComponent;
